/**
 * File: ResizableLayout.tsx
 * Description: Resizable layout component using react-resizable-panels
 * Author: System
 * Created: 2025-09-04
 * Last Modified: 2025-01-04
 */

import { ChatBubbleLeftRightIcon, XMarkIcon, Bars3Icon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState, type ReactNode } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useLocation } from 'react-router-dom';

import { projectService } from '../../services/projectService';
import { ACTIVE_PROJECT_EVENT } from '../../utils/activeProject';
import { ChatInterface } from '../chat/ChatInterface';

interface ResizableLayoutProps {
  children: ReactNode;
  showChat?: boolean;
  defaultChatSize?: number;
  minChatSize?: number;
  maxChatSize?: number;
  className?: string;
}

/**
 * ResizableLayout - Layout component with resizable panels for main content and chat
 */
export const ResizableLayout: React.FC<ResizableLayoutProps> = ({
  children,
  showChat = false,
  defaultChatSize = 20,
  minChatSize = 20,
  maxChatSize = 60,
  className = '',
}) => {
  const [isChatVisible, setIsChatVisible] = useState(showChat);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [clearHistoryFn, setClearHistoryFn] = useState<(() => void) | undefined>(undefined);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const location = useLocation();

  // Extract project ID from the URL. On non-project pages it stays null until
  // the user explicitly picks a project from the in-chat project list.
  const [projectId, setProjectId] = useState<number | null>(() => {
    const projectMatch = /\/project\/(\d+)/.exec(location.pathname);
    if (projectMatch) {return Number.parseInt(projectMatch[1], 10);}
    const searchParams = new URLSearchParams(location.search);
    const projectIdParam = searchParams.get('projectId');
    if (projectIdParam) {return Number.parseInt(projectIdParam, 10);}
    return null;
  });

  useEffect(() => {
    const projectMatch = /\/project\/(\d+)/.exec(location.pathname);
    if (projectMatch) {
      setProjectId(Number.parseInt(projectMatch[1], 10));
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const projectIdParam = searchParams.get('projectId');
    if (projectIdParam) {
      setProjectId(Number.parseInt(projectIdParam, 10));
      return;
    }
    setProjectId(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Keep the header in sync when the active project changes from the chat.
    const handleActiveProjectChange = (event: Event) => {
      const detail = (event as CustomEvent<number>).detail;
      if (typeof detail === 'number' && !Number.isNaN(detail)) {
        setProjectId(detail);
      }
    };
    globalThis.addEventListener(ACTIVE_PROJECT_EVENT, handleActiveProjectChange);
    return () => {
      globalThis.removeEventListener(ACTIVE_PROJECT_EVENT, handleActiveProjectChange);
    };
  }, []);

  // Resolve the human-readable project name for the chat header.
  const [projectName, setProjectName] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setProjectName(null);
      return;
    }
    let cancelled = false;
    projectService
      .getProject(projectId)
      .then((project) => {
        if (!cancelled) {
          setProjectName(project.title);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProjectName(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const toggleChat = () => {
    setIsChatVisible(!isChatVisible);
  };

  const handleChatCollapse = () => {
    setIsChatCollapsed(true);
  };

  const handleChatExpand = () => {
    setIsChatCollapsed(false);
  };

  const handleClearHistoryChange = (clearHistory: (() => void) | undefined, isLoading: boolean) => {
    setClearHistoryFn(() => clearHistory);
    setIsLoadingHistory(isLoading);
  };

  const handleClearHistory = () => {
    if (clearHistoryFn) {
      clearHistoryFn();
    }
  };

  // If chat is not visible, show main content with floating toggle button
  if (!isChatVisible) {
    return (
      <div className={`relative h-full ${className}`}>
        {/* Main content takes full width */}
        <div className="h-full">
          {children}
        </div>
        
        {/* Floating chat toggle button */}
        <button
          onClick={toggleChat}
          className="fixed bottom-6 right-6 z-50 bg-primary hover:bg-primary-focus text-primary-content rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 group main-button"
          title="Open Archie AI Assistant"
          aria-label="Open Archie AI Assistant"
        >
          <ChatBubbleLeftRightIcon className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
        </button>
      </div>
    );
  }

  return (
    <div className={`h-full ${className}`}>
      <PanelGroup direction="horizontal" className="h-full">
        {/* Main Content Panel */}
        <Panel 
          defaultSize={100 - defaultChatSize}
          minSize={100 - maxChatSize}
          maxSize={100 - minChatSize}
          className="flex flex-col min-h-0"
        >
          <div className="h-full">
            {children}
          </div>
        </Panel>

        {/* Resize Handle */}
        <PanelResizeHandle className="w-2 bg-base-300 hover:bg-primary/20 active:bg-primary/30 transition-colors duration-200 cursor-col-resize relative group">
          {/* Visual indicator line */}
          <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-0.5 bg-base-content/20 group-hover:bg-primary/50 transition-colors duration-200" />
          
          {/* Resize icon - centered and responsive */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <Bars3Icon className="w-4 h-4 text-base-content/60 group-hover:text-primary rotate-90 transition-colors duration-200" />
          </div>
          
          {/* Alternative: Drag handle dots for subtle indication */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col space-y-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="w-1 h-1 bg-primary rounded-full" />
            <div className="w-1 h-1 bg-primary rounded-full" />
            <div className="w-1 h-1 bg-primary rounded-full" />
          </div>
        </PanelResizeHandle>

        {/* Chat Panel */}
        <Panel 
          defaultSize={defaultChatSize}
          minSize={minChatSize}
          maxSize={maxChatSize}
          collapsible={true}
          collapsedSize={4}
          onCollapse={handleChatCollapse}
          onExpand={handleChatExpand}
          className="flex flex-col bg-base-100"
        >
          {/* Chat Panel Header */}
          {!isChatCollapsed ? (
            <div className="flex items-center justify-between p-4 bg-base-200 border-b border-base-300 shadow-sm">
              <div 
                className="flex items-center gap-2"
                title={projectId ? `Project #${projectId}` : 'AI Assistant for architecture decisions and project context'}
              >
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-primary" />
                <div className="flex flex-col">
                  <h3 className="font-semibold text-base-content">Archie AI Assistant</h3>
                  <span className="text-xs text-base-content/60">
                    {projectName
                      ? `Project: ${projectName}`
                      : 'No project selected'}
                  </span>
                </div>
                {isLoadingHistory && (
                  <span className="text-sm text-base-content/60">Loading...</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Delete Chat Button */}
                {clearHistoryFn && (
                  <button
                    onClick={handleClearHistory}
                    className="btn btn-ghost btn-sm btn-circle hover:bg-base-300"
                    title="Delete chat"
                    aria-label="Delete chat"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
                {/* Close Button */}
                <button
                  onClick={toggleChat}
                  className="btn btn-ghost btn-sm btn-circle hover:bg-base-300"
                  title="Close Chat"
                  aria-label="Close Chat"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full w-full bg-base-200">
              <ChatBubbleLeftRightIcon 
                className="w-6 h-6 text-primary transform -rotate-90" 
                title="Chat (Click to expand)"
              />
            </div>
          )}

          {/* Chat Content */}
          {!isChatCollapsed && (
            <div className="flex-1 overflow-hidden bg-base-50 min-h-0">
              <ChatInterface 
                className="h-full" 
                onClearHistoryChange={handleClearHistoryChange}
              />
            </div>
          )}
        </Panel>
      </PanelGroup>
    </div>
  );
};
